import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  UploadFileRounded,
  LinkRounded,
  OpenInNewRounded,
  PlaylistAddRounded,
  OndemandVideoRounded,
  DescriptionRounded,
  MenuBookRounded,
} from '@mui/icons-material';
import { useState, useRef } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  courseMaterialApi,
  type MaterialType,
  type SectionMaterialDto,
  type ExternalSource,
} from '../../../lib/api/course-material.api';
import { toEmbedUrl, isPlayableVideoFile } from '../../portal-v2/util/video-embed';
import { errorText } from '../../../lib/error-text';

// Files allowed by Storage rules.
const ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'image/webp',
];
const MAX_BYTES = 50 * 1024 * 1024;

export const TYPE_META: Record<
  MaterialType,
  { label: string; icon: React.ReactNode; color: string }
> = {
  video: { label: 'Video', icon: <OndemandVideoRounded fontSize="small" />, color: '#ef4444' },
  notes: { label: 'Apuntes', icon: <DescriptionRounded fontSize="small" />, color: '#3b82f6' },
  reading: { label: 'Lectura', icon: <MenuBookRounded fontSize="small" />, color: '#10b981' },
};

export function detectExternalSource(url: string): ExternalSource {
  const u = url.toLowerCase();
  if (u.includes('zoom.us')) return 'zoom';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('vimeo.com')) return 'vimeo';
  return 'other';
}

function defaultTitleFromUrl(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.replace(/^www\./, '');
    if (host.includes('youtube.com') || host === 'youtu.be') {
      return 'Video YouTube';
    }
    if (host.endsWith('vimeo.com')) {
      return 'Video Vimeo';
    }
    if (host.endsWith('zoom.us')) {
      return 'Grabación Zoom';
    }
    return u.pathname.split('/').filter(Boolean).pop() ?? host;
  } catch {
    return 'Recurso externo';
  }
}

function isImageMaterial(m: SectionMaterialDto): boolean {
  if (m.mimeType?.startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|gif)$/i.test(m.originalFileName ?? '');
}

export function PreviewBody({
  material,
  url,
}: {
  material: SectionMaterialDto;
  url: string;
}): JSX.Element {
  // External link (video / zoom / youtube / vimeo).
  if (material.url) {
    if (isPlayableVideoFile(url)) {
      return (
        <Box
          component="video"
          src={url}
          controls
          sx={{ width: '100%', maxHeight: '74vh', borderRadius: 1, bgcolor: '#000' }}
        />
      );
    }
    const embed = toEmbedUrl(url);
    if (embed) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: `${(1 / (embed.aspectRatio ?? 16 / 9)) * 100}%`,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#000',
          }}
        >
          <Box
            component="iframe"
            src={embed.src}
            {...(embed.allow ? { allow: embed.allow } : {})}
            allowFullScreen
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        </Box>
      );
    }
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Este enlace no se puede previsualizar embebido.
        </Typography>
        <Button
          variant="contained"
          startIcon={<OpenInNewRounded />}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Abrir en pestaña nueva
        </Button>
      </Box>
    );
  }
  // Stored file.
  if (isImageMaterial(material)) {
    return (
      <Box textAlign="center">
        <img src={url} alt={material.title} style={{ maxWidth: '100%', borderRadius: 6 }} />
      </Box>
    );
  }
  return (
    <iframe
      title={material.title}
      src={url}
      style={{ width: '100%', height: '74vh', border: 'none', borderRadius: 6 }}
    />
  );
}

// ─── Upload file dialog ────────────────────────────────────────────────────

interface UploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (createdIds: string[]) => void;
  sectionId: string;
}

export function UploadMaterialDialog({
  open,
  onClose,
  onSuccess,
  sectionId,
}: UploadProps): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const [type, setType] = useState<MaterialType>('notes');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setType('notes');
    setTitle('');
    setDescription('');
    setFile(null);
    setErr(null);
  };

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!title.trim()) {
      setErr('Título obligatorio');
      return;
    }
    if (!file) {
      setErr('Selecciona un archivo');
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr(`Tamaño máximo: 50 MB (este archivo: ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setErr('Tipo no permitido. Usa PDF, Word o imagen (PNG/JPG/WEBP).');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const materialId = crypto.randomUUID();
      const safeName = file.name.replace(/[^\w.\-]/g, '_');
      const storagePath = `tenants/${currentTenantId}/materials/${sectionId}/${materialId}/${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const created = await courseMaterialApi.create({
        tenantId: currentTenantId,
        sectionId,
        type,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        storagePath,
        mimeType: file.type,
        sizeBytes: file.size,
        originalFileName: file.name,
      });
      reset();
      onSuccess([created.id]);
    } catch (e: unknown) {
      setErr(errorText(e, 'Error al subir archivo'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Subir archivo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <FormControl size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={type}
              onChange={(e) => setType(e.target.value as MaterialType)}
            >
              <MenuItem value="notes">Apuntes</MenuItem>
              <MenuItem value="reading">Lectura</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Título"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Descripción (opcional)"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 2000 }}
          />
          <Box>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ALLOWED_MIME.join(',')}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => inputRef.current?.click()}
            >
              {file ? `Cambiar archivo (${file.name})` : 'Seleccionar archivo'}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Máx 50 MB. Tipos: PDF, Word, imagen.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <UploadFileRounded />}
        >
          {submitting ? 'Subiendo…' : 'Publicar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add link dialog ───────────────────────────────────────────────────────

interface LinkProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (createdIds: string[]) => void;
  sectionId: string;
}

export function LinkMaterialDialog({
  open,
  onClose,
  onSuccess,
  sectionId,
}: LinkProps): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const [type, setType] = useState<MaterialType>('video');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setType('video');
    setTitle('');
    setDescription('');
    setUrl('');
    setErr(null);
  };

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!title.trim() || !url.trim()) {
      setErr('Título y URL obligatorios');
      return;
    }
    try {
      new URL(url.trim());
    } catch {
      setErr('URL no válida');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const created = await courseMaterialApi.create({
        tenantId: currentTenantId,
        sectionId,
        type,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        url: url.trim(),
        externalSource: detectExternalSource(url),
      });
      reset();
      onSuccess([created.id]);
    } catch (e: unknown) {
      setErr(errorText(e, 'Error al guardar enlace'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar enlace</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <FormControl size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={type}
              onChange={(e) => setType(e.target.value as MaterialType)}
            >
              <MenuItem value="video">Video (Zoom, YouTube, etc.)</MenuItem>
              <MenuItem value="reading">Lectura (URL externa)</MenuItem>
              <MenuItem value="notes">Apuntes (URL externa)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Título"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="URL"
            size="small"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://zoom.us/..."
          />
          <TextField
            label="Descripción (opcional)"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            inputProps={{ maxLength: 2000 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <LinkRounded />}
        >
          {submitting ? 'Guardando…' : 'Publicar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Bulk link dialog (paste N URLs at once) ───────────────────────────────

export function BulkLinkMaterialDialog({
  open,
  onClose,
  onSuccess,
  sectionId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (createdIds: string[]) => void;
  sectionId: string;
}): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const [type, setType] = useState<MaterialType>('video');
  const [csv, setCsv] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<{
    ok: number;
    failed: { url: string; reason: string }[];
  } | null>(null);

  const reset = () => {
    setType('video');
    setCsv('');
    setErr(null);
    setReport(null);
  };

  const parsedRows = csv
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // Format: "<url>" OR "<url>|<title>". Pipe separator is intentional —
      // commas are common inside YouTube titles.
      const [rawUrl, ...rest] = line.split('|');
      const url = (rawUrl ?? '').trim();
      const title = rest.join('|').trim() || defaultTitleFromUrl(url);
      return { url, title };
    });

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (parsedRows.length === 0) {
      setErr('Pega al menos una URL.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    setReport(null);
    const failed: { url: string; reason: string }[] = [];
    const createdIds: string[] = [];
    let ok = 0;
    for (const row of parsedRows) {
      try {
        new URL(row.url);
      } catch {
        failed.push({ url: row.url || '(vacío)', reason: 'URL no válida' });
        continue;
      }
      try {
        const created = await courseMaterialApi.create({
          tenantId: currentTenantId,
          sectionId,
          type,
          title: row.title.slice(0, 200),
          url: row.url,
          externalSource: detectExternalSource(row.url),
        });
        createdIds.push(created.id);
        ok += 1;
      } catch (e) {
        failed.push({ url: row.url, reason: errorText(e, 'error') });
      }
    }
    setReport({ ok, failed });
    setSubmitting(false);
    if (failed.length === 0) {
      reset();
      onSuccess(createdIds);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar varios enlaces</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          {report && (
            <Alert severity={report.failed.length === 0 ? 'success' : 'warning'}>
              {report.ok} importados correctamente
              {report.failed.length > 0 && `, ${report.failed.length} fallidos`}.
              {report.failed.length > 0 && (
                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                  {report.failed.map((f, i) => (
                    <li key={i}>
                      <code>{f.url}</code> — {f.reason}
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <FormControl size="small">
            <InputLabel>Tipo para todos</InputLabel>
            <Select
              label="Tipo para todos"
              value={type}
              onChange={(e) => setType(e.target.value as MaterialType)}
            >
              <MenuItem value="video">Video (Zoom, YouTube, etc.)</MenuItem>
              <MenuItem value="reading">Lectura (URL externa)</MenuItem>
              <MenuItem value="notes">Apuntes (URL externa)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Pega URLs (una por línea — opcional título tras | )"
            multiline
            minRows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={
              'https://us02web.zoom.us/rec/share/xxx\n' +
              'https://www.youtube.com/watch?v=abc | Clase 1 — introducción\n' +
              'https://vimeo.com/123456'
            }
            fullWidth
          />

          {parsedRows.length > 0 && (
            <Alert severity="info">
              Se importarán <strong>{parsedRows.length}</strong> enlace
              {parsedRows.length === 1 ? '' : 's'} en la sección actual con tipo{' '}
              <strong>{type}</strong>.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || parsedRows.length === 0}
          startIcon={submitting ? <CircularProgress size={16} /> : <PlaylistAddRounded />}
        >
          {submitting ? 'Importando…' : `Importar ${parsedRows.length || ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
